import { HttpErrorResponse } from '@angular/common/http';

import { metaAdapter, metaInitState, paginationInitState } from '../meta-adapter';
import { PaginationState } from '../types';
import {
  BaseEntity,
  EntityMetaAdapter,
  EntityState,
  MetaActionOptions,
  MetaScopeOptions,
} from './types';
import { resolveScope } from './utils';

const changeMetaBoolean = (key: 'markAsPending' | 'markAsLoaded') => <T extends BaseEntity>(
  state: EntityState<T>,
  options?: MetaActionOptions,
) => {
  const ids = options?.entities || [];
  const scope = options?.scope || null;

  const entityMetaMap = ids.length
    ? {
        ...state.entityMetaMap,
        ...Object.fromEntries(
          ids.map(id => [id, metaAdapter[key](state.entityMetaMap[id] || metaInitState)]),
        ),
      }
    : state.entityMetaMap;

  const scopeMeta = scope
    ? {
        ...state.scopeMeta,
        [scope]: metaAdapter[key](state.scopeMeta[scope] || metaInitState),
      }
    : state.scopeMeta;

  return {
    ...state,
    entityMetaMap,
    scopeMeta,
  };
};

export const changePaginationMeta = <T extends BaseEntity>(
  state: EntityState<T>,
  paginationState: PaginationState,
  props: MetaScopeOptions,
) => {
  const scope = resolveScope(props?.scope);
  const scopePagination = {
    ...state.scopePagination,
    [scope]: { ...state.scopePagination[scope], ...paginationState } || paginationInitState,
  };

  return { ...state, scopePagination };
};

export const getEntityMetaAdapter = <T extends BaseEntity>() => {
  const entityMetaAdapter: EntityMetaAdapter<T> = {
    markAsPending: changeMetaBoolean('markAsPending'),
    markAsLoaded: changeMetaBoolean('markAsLoaded'),
    setPaginationMeta: changePaginationMeta,
    setError: (state: EntityState<T>, error: HttpErrorResponse, options: MetaActionOptions) => {
      const entities = options.entities || [];
      const scope = options.scope || null;

      const entityMetaMap = entities.length
        ? {
            ...state.entityMetaMap,
            ...Object.fromEntries(
              entities.map(entityId => [
                entityId,
                metaAdapter.setError(state.entityMetaMap[entityId] || metaInitState, error),
              ]),
            ),
          }
        : state.entityMetaMap;

      const scopeMeta = scope
        ? {
            ...state.scopeMeta,
            [scope]: metaAdapter.setError(state.scopeMeta[scope] || metaInitState, error),
          }
        : state.scopeMeta;

      return {
        ...state,
        entityMetaMap,
        scopeMeta,
      };
    },
    // sets alias for correlationId -> remoteId
    setAlias: (state: EntityState<T>, mapping: any) => ({
      ...state,
      aliases: {
        ...mapping,
        ...state.aliases,
      },
    }),
  };
  return entityMetaAdapter;
};
